import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { getSupabaseClient } from './providers'
import { serverMode } from './network'
import { useAppStore } from './store'
import type { Draft } from './types'

export function BetaSessionGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [status, setStatus] = useState<'loading' | 'onboarding' | 'signed-in' | 'signed-out' | 'misconfigured'>(serverMode ? 'loading' : 'signed-in')

  useEffect(() => {
    if (!serverMode) return
    let active = true
    let unsubscribe: () => void = () => {}
    void getSupabaseClient().then(async (client) => {
      if (!client) { if (active) setStatus('misconfigured'); return }
      const { data } = await client.auth.getSession()
      let onboardingComplete = true
      if (data.session) {
        const [likes, saves, follows, blocks, mutes, profile] = await Promise.all([
          client.from('likes').select('loop_id'), client.from('saves').select('loop_id'), client.from('follows').select('followed_id').eq('follower_id', data.session.user.id), client.from('blocks').select('blocked_id'), client.from('mutes').select('muted_id'), client.from('profiles').select('onboarding_completed_at').eq('id', data.session.user.id).maybeSingle()
        ])
        onboardingComplete = Boolean(profile.data?.onboarding_completed_at)
        useAppStore.setState({
          currentUserId: data.session.user.id,
          liked: (likes.data ?? []).map((row) => row.loop_id), saved: (saves.data ?? []).map((row) => row.loop_id), followed: (follows.data ?? []).map((row) => row.followed_id), blockedProfiles: (blocks.data ?? []).map((row) => row.blocked_id), mutedProfiles: (mutes.data ?? []).map((row) => row.muted_id)
        })
        const drafts = await client.from('drafts').select('payload').order('updated_at', { ascending: false }).limit(50)
        if (!drafts.error) {
          const recovered: Draft[] = []
          for (const row of drafts.data ?? []) {
            const draft = row.payload as Draft
            const assetIds = [...new Set((draft.segments ?? []).flatMap((segment) => segment.mediaAssetId ? [segment.mediaAssetId] : []))]
            const assets = assetIds.length ? await client.from('media_assets').select('id,storage_bucket,storage_path').in('id', assetIds) : { data: [], error: null }
            const urls = new Map<string, string>()
            for (const asset of assets.data ?? []) {
              if (!asset.storage_path) continue
              if (import.meta.env.VITE_MEDIA_PIPELINE_MODE === 'direct' && asset.storage_bucket === 'loop-media') urls.set(asset.id, client.storage.from('loop-media').getPublicUrl(asset.storage_path).data.publicUrl)
              else { const signed = await client.storage.from(asset.storage_bucket ?? 'loop-raw').createSignedUrl(asset.storage_path, 3600); if (signed.data?.signedUrl) urls.set(asset.id, signed.data.signedUrl) }
            }
            recovered.push({ ...draft, segments: draft.segments?.map((segment) => ({ ...segment, mediaUrl: segment.mediaAssetId ? urls.get(segment.mediaAssetId) ?? segment.mediaUrl : segment.mediaUrl })) })
          }
          useAppStore.setState((state) => ({ drafts: [...new Map([...state.drafts.map((draft) => [draft.id, draft] as const), ...recovered.map((draft) => [draft.id, draft] as const)]).values()] }))
        }
      }
      if (active) setStatus(data.session ? onboardingComplete ? 'signed-in' : 'onboarding' : 'signed-out')
      const listener = client.auth.onAuthStateChange((event, session) => {
        if (!active) return
        if (!session) setStatus('signed-out')
        else if (event === 'SIGNED_IN') {
          const marker = `loop6-session-${session.user.id}`
          if (!sessionStorage.getItem(marker)) { sessionStorage.setItem(marker, 'ready'); window.location.replace('/') }
        }
      })
      unsubscribe = () => listener.data.subscription.unsubscribe()
    })
    return () => { active = false; unsubscribe() }
  }, [])

  if (status === 'loading') return <div className="route-loading"><i /><span>VERIFYING BETA SESSION</span></div>
  if (status === 'misconfigured') return <div className="route-loading"><span>BETA CONFIGURATION IS INCOMPLETE</span><p>Server mode never falls back to device-only demo data.</p></div>
  if (status === 'onboarding' && location.pathname !== '/onboarding') return <Navigate to="/onboarding" replace />
  if (status === 'signed-out' && location.pathname !== '/auth') return <Navigate to="/auth" replace state={{ from: location.pathname }} />
  if (status === 'signed-in' && (location.pathname === '/auth' || location.pathname === '/onboarding')) return <Navigate to="/" replace />
  return children
}
